import type { ReactNode } from 'react'
import { cn } from '../../lib/utils'
import { Badge } from '../ui/badge'
import { levelMeta, toAlertLevel } from '../../lib/alert-level'

export function LevelBadge({ alertLevel, severity, className, pulse }: {
  alertLevel?: string | null
  severity?: number | null
  className?: string
  pulse?: boolean
}) {
  const level = toAlertLevel(alertLevel, severity)
  const meta = levelMeta[level]
  return (
    <Badge
      variant={level === 'info' ? 'muted' : level}
      className={cn(meta.glow, pulse && 'animate-emergency-pulse', className)}
    >
      {meta.label}
    </Badge>
  )
}

export function StatusBadge({ status, labels }: { status: string; labels?: Record<string, string> }) {
  const variant =
    status === 'confirmed' || status === 'approved' || status === 'done' || status === 'published' || status === 'online'
      ? 'success'
      : status === 'pending' || status === 'assigned'
        ? 'watch'
        : status === 'rejected' || status === 'timeout' || status === 'offline'
          ? 'critical'
          : 'muted'
  const label = labels?.[status] ?? status
  return <Badge variant={variant}>{label}</Badge>
}

export function EmptyState({ title, description, icon, action }: {
  title: string
  description?: string
  icon?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 px-6 py-10 text-center">
      {icon && <div className="text-muted-foreground/40">{icon}</div>}
      <div className="text-sm font-medium text-foreground/80">{title}</div>
      {description && <div className="max-w-sm text-xs leading-5 text-muted-foreground">{description}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

export function ErrorState({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <div className="flex min-h-[120px] flex-col items-center justify-center gap-2 px-6 py-8 text-center">
      <div className="text-sm text-critical">{message}</div>
      {retry && (
        <button onClick={retry} className="text-xs text-primary hover:underline">
          重试
        </button>
      )}
    </div>
  )
}
